// The slideshow engine behind all three heroes: the front door's
// (HeroMosaic, 30 Jul 2026), Discover's and the feed's (DiscoverHero and
// FeedHero, 31 Jul 2026). One thing at a time, large, cross-fading to the
// next, each slide holding for as long as it takes to read it.
//
// Everything here is about the clock. The hold is banked rather than
// restarted whenever the slideshow pauses, so a glance away, an open modal,
// or a scroll down the page never costs the reader the sentence they were
// halfway through. Pauses stack by reason: two causes can overlap, and the
// slides only move again when the last one lifts.
import { useEffect, useRef, useState } from "react";
import { artImg } from "../assets.js";

export interface Slide {
  // Stable per slide, so a deck can be kept in step with a changing set
  // without reshuffling it. Not the image: a practice with no art of its own
  // borrows its topic's stand-in, and two of those collide.
  id: string;
  // The line the slide is built around: a practice on the front door and
  // Discover, an article title on the feed. Its length sets the hold.
  text: string;
  image: string;
  // Extra hold for this slide alone, on top of whatever its length earns and
  // whatever pace the surface has set (2 Aug 2026, Rob). A Crossfire slide
  // takes it: the question is the same amount of reading as a practice, and
  // then there are two or three names under it that a practice does not have.
  holdBonusMs?: number;
}

// Long enough to read the practice, notice who it came from, and look at the
// picture before the next one arrives. A carousel that outruns its own
// sentence is just motion.
//
// The hold is set by the length of the practice, not by a single number for
// every slide: "Cut down on canned food" and "Stop chasing post-workout
// carbohydrate; hit your daily carbohydrate total instead" are not the same
// amount of reading, and one fixed beat either rushes the long ones or
// strands the short ones. The two cut-offs sit at the front-door manifest's
// own thirds (23 to 80 characters, a third under 58 and a third over 73), so
// the three speeds get roughly even use; re-check them when it is rebuilt.
const HOLD_SHORT_MS = 6000;
const HOLD_MID_MS = 7000;
const HOLD_LONG_MS = 8000;
const MID_CHARS = 58;
const LONG_CHARS = 73;
export const FADE_MS = 1400;

/* What the heroes actually measure, read off the stylesheet (3 Aug 2026).

   One constant was wrong for both of the containers using it, because they are
   not the same width and are not close. `.stage.solo` on the front door is a
   single column capped at 1360 with 22px of padding, so 1316. `.stage` in the
   app is `296px minmax(0,1fr) 296px` with a 20px gap inside the same cap, so
   the middle column is 684 at its widest and less below 1320. Claiming 1000
   for both over-fetched on every in-app hero on a desktop, asking for the
   1200-wide original to paint 684 points.

   The front-door one is also the LCP element of the most public page in the
   product, which is the one place to round up rather than down. */
export const HERO_SIZES_SOLO = "(max-width: 1404px) 100vw, 1316px";
export const HERO_SIZES = "(max-width: 920px) 100vw, 720px";

// Set as a sentence, with the full stop the stored action does not carry:
// everywhere else the action is a check-list row, but on a slide it is the
// largest line of type on the screen and an unpunctuated one reads as a
// label. Anything that already ends in punctuation is left alone.
export function sentence(text: string): string {
  return /[.!?]$/.test(text.trim()) ? text : `${text}.`;
}

// `scale` lets a surface take its own pace: the feed runs slower than the
// practice heroes (31 Jul 2026, Rob), because an article headline is a
// different kind of read from a one-line instruction. `bonus` adds a flat
// number of milliseconds on top, which is the right shape for "a second
// longer" (31 Jul 2026, Rob, on Discover): a multiplier would have stretched
// the long slides more than the short ones, and length is already accounted
// for above.
function holdFor(slide: Slide | undefined, scale = 1, bonus = 0): number {
  if (!slide) return HOLD_MID_MS * scale + bonus;
  const extra = bonus + (slide.holdBonusMs ?? 0);
  if (slide.text.length <= MID_CHARS) return HOLD_SHORT_MS * scale + extra;
  if (slide.text.length <= LONG_CHARS) return HOLD_MID_MS * scale + extra;
  return HOLD_LONG_MS * scale + extra;
}

// Absent under jsdom, and a hero is not worth crashing a render over: with no
// matchMedia the slide renders and stays still.
function mql(query: string): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query)
    : null;
}

export function shuffled<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

// The words land in a different corner each slide, so a run of them does not
// read as one template refilled. Never the same corner twice running, which
// is what makes it look chosen rather than accidental.
const CORNERS = ["tl", "tr", "bl", "br"] as const;
export type Corner = (typeof CORNERS)[number];

function nextCorner(from: Corner): Corner {
  const options = CORNERS.filter((c) => c !== from);
  return options[Math.floor(Math.random() * options.length)] ?? "bl";
}

// The first slide picks its corner too (31 Jul 2026, Rob): starting bottom
// left every time made the rotation look like it began after the first
// picture rather than with it.
function firstCorner(): Corner {
  return CORNERS[Math.floor(Math.random() * CORNERS.length)] ?? "bl";
}

export function useSlideshow<T extends Slide>(
  slides: T[],
  {
    paused = false,
    holdScale = 1,
    holdBonusMs = 0,
    // The preloader has to warm the same srcset candidate the render will
    // ask for, so it needs the caller's own sizes: the front door's hero is
    // nearly twice the width of the in-app ones, and a preload of the wrong
    // one is a wasted fetch followed by the real one.
    sizes = HERO_SIZES,
  }: {
    paused?: boolean;
    holdScale?: number;
    holdBonusMs?: number;
    sizes?: string;
  } = {},
) {
  // Played in the order given, and never reordered afterwards: a set that
  // changes under the reader (they decide on a practice and it leaves
  // Discover) must not send the deck back to the beginning. Choosing what is
  // in the deck, and in what order, belongs to each hero: the front door
  // shuffles its manifest, the in-app ones draw a fresh sample per visit.
  const deck = useRef<T[]>([...slides]);
  const at = useRef(0);
  const [cur, setCur] = useState<T | undefined>(() => deck.current[0]);
  const [prev, setPrev] = useState<T | null>(null);
  const [corner, setCorner] = useState<Corner>(firstCorner);
  const fade = useRef(0);
  const clock = useRef<{
    pause: (reason: string) => void;
    resume: (reason: string) => void;
    advance: () => void;
    settle: () => void;
    show: (id: string) => void;
  } | null>(null);

  const ids = slides.map((s) => s.id).join("|");
  useEffect(() => {
    const want = new Map(slides.map((s) => [s.id, s]));
    const showing = deck.current[at.current]?.id;
    // Held slides are re-read from the incoming set rather than kept as they
    // were, so an edit upstream reaches the picture without disturbing the
    // order.
    const kept = deck.current.filter((s) => want.has(s.id)).map((s) => want.get(s.id) as T);
    const fresh = slides.filter((s) => !deck.current.some((d) => d.id === s.id));
    const moved = fresh.length > 0 || kept.length !== deck.current.length;
    deck.current = [...kept, ...fresh];
    if (!moved) return;
    const idx = deck.current.findIndex((s) => s.id === showing);
    if (idx >= 0) {
      at.current = idx;
      return;
    }
    // The slide on screen is the one that just left the set, which on
    // Discover means the reader decided on it. Move on rather than hold a
    // practice that is no longer theirs to decide, even when what remains
    // is a single slide.
    at.current = Math.min(at.current, Math.max(0, deck.current.length - 1));
    clock.current?.settle();
    // Keyed on the id list rather than the array: a fresh array holding the
    // same practices is the same deck, and the screen builds a new one on
    // every render.
  }, [ids, slides]);

  useEffect(() => {
    const motion = mql("(prefers-reduced-motion: reduce)");
    if (!motion || motion.matches) return;
    let tick = 0;
    // The clock is kept as a start stamp plus a length rather than just a
    // timeout id, so a pause can bank what was left of the hold and hand it
    // back on return instead of starting the slide over.
    let holdMs = 0;
    let startedAt = 0;
    let banked = 0;
    const stopped = new Set<string>();
    const run = (ms: number) => {
      holdMs = ms;
      startedAt = Date.now();
      window.clearTimeout(tick);
      tick = window.setTimeout(advance, ms);
    };
    // Land on a slide: the one move, however it was asked for.
    const showAt = (idx: number) => {
      const slide = deck.current[idx];
      if (!slide) return;
      at.current = idx;
      // The one after this is fetched now, so the fade never lands on a
      // frame that is still downloading.
      const after = deck.current[(idx + 1) % deck.current.length];
      if (after) {
        const img = new Image();
        /* srcset and sizes before src, and both of them: the slides became
           <img> on 3 Aug 2026, so preloading the 1200-wide original here would
           warm the wrong entry and a phone would still pay for the variant it
           actually paints. Set in this order because the browser resolves the
           candidate when src is assigned. */
        const next = artImg(after.image);
        if (next.srcSet) {
          img.srcset = next.srcSet;
          img.sizes = sizes;
        }
        img.src = next.src;
      }
      setCur((c) => {
        setPrev(c ?? null);
        return slide;
      });
      setCorner((c) => nextCorner(c));
      window.clearTimeout(fade.current);
      fade.current = window.setTimeout(() => setPrev(null), FADE_MS);
      run(holdFor(slide, holdScale, holdBonusMs));
    };
    const advance = () => {
      // One slide is not a slideshow, but the clock keeps beating rather
      // than stopping dead (31 Jul 2026): a deck that shrinks to one and is
      // then topped up would otherwise never move again, because nothing
      // else reschedules it.
      if (deck.current.length < 2) {
        run(holdFor(deck.current[at.current], holdScale, holdBonusMs));
        return;
      }
      showAt((at.current + 1) % deck.current.length);
    };
    // The slide on screen has left the set, so the picture has to move even
    // if what is left is a single slide (31 Jul 2026, Rob: deciding on one
    // of Today's two froze the hero on the one just decided). advance would
    // not, which is right for the clock and wrong for this.
    const settle = () => {
      if (!deck.current.length) {
        setCur(undefined);
        setPrev(null);
        return;
      }
      showAt(Math.min(at.current, deck.current.length - 1));
    };
    // Jump straight to one slide: the feed opens on the article the reader
    // picked on Today (31 Jul 2026, Rob). It fades in like any other slide
    // and takes a full hold, because it is the one they asked for.
    const show = (id: string) => {
      const idx = deck.current.findIndex((s) => s.id === id);
      if (idx < 0 || idx === at.current) return;
      showAt(idx);
    };
    const pause = (reason: string) => {
      if (stopped.size === 0) {
        banked = Math.max(0, holdMs - (Date.now() - startedAt));
        window.clearTimeout(tick);
      }
      stopped.add(reason);
    };
    const resume = (reason: string) => {
      if (!stopped.delete(reason) || stopped.size > 0) return;
      run(banked);
    };
    clock.current = { pause, resume, advance, settle, show };
    run(holdFor(deck.current[at.current], holdScale, holdBonusMs));

    // Nothing the pointer does stops the slides (31 Jul 2026): pausing on
    // hover was tried on the whole picture, then on the caption, then on the
    // byline alone, and every version read as a jam rather than a courtesy,
    // because a hero this size is where the pointer rests. Leaving is what
    // pauses: the tab goes to the background, or the window loses focus, both
    // of which happen when a byline link opens the expert's page. Coming back
    // gives the slide the rest of its hold, not a fresh one, so a glance away
    // does not cost the sentence you were reading.
    //
    // Banking the remainder is also what makes the return prompt: a timer left
    // pending in a hidden tab is throttled to once a second and eventually
    // once a minute, so whatever it had scheduled may be long overdue.
    //
    // Two reasons, not one (2 Aug 2026, Rob: "if I switch off the browser and
    // then switch focus back to chrome, the slideshow does not resume, I have
    // to click or something"). Losing the window and hiding the tab are
    // separate events that fire in either order, and they used to share the
    // reason "away" while resume() deleted that reason BEFORE checking whether
    // the document was visible again. Come back to Chrome and focus arrives
    // first, while the document is still hidden: the reason was consumed, the
    // early return skipped the restart, and the visibilitychange that followed
    // found nothing left to delete and returned too. The clock was dead until
    // something else happened to call run(). As two independent reasons the
    // order stops mattering, because whichever event arrives last is the one
    // that empties the set and restarts the hold.
    const away = () => pause("away");
    const back = () => resume("away");
    const onVisibility = () => {
      if (document.visibilityState === "visible") resume("hidden");
      else pause("hidden");
    };
    window.addEventListener("blur", away);
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", onVisibility);
    // A page that opens in a background tab starts paused, rather than
    // spending its first slides where nobody is looking.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") pause("hidden");
    return () => {
      clock.current = null;
      window.clearTimeout(tick);
      window.clearTimeout(fade.current);
      window.removeEventListener("blur", away);
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // holdScale is read once, at mount: a surface picks a pace, it does not
    // change it mid-run.
  }, []);

  // The host's own reasons to hold still: a modal is up, or the pictures have
  // scrolled off the top of the screen.
  useEffect(() => {
    if (paused) clock.current?.pause("host");
    else clock.current?.resume("host");
  }, [paused]);

  const show = (id: string) => clock.current?.show(id);
  // Turning the deck by hand: the hold restarts on the slide it lands on, so
  // a reader stepping through never gets half a beat on the next one.
  const next = () => clock.current?.advance();
  return { cur, prev, corner, show, next };
}

// The byline drops what will not fit rather than letting it be cut off (31
// Jul 2026, Rob): on a phone a long institution name plus a grade plus a time
// runs past the frame, and a half-shown evidence label is worse than none.
// Marked parts go in reverse order, the least load-bearing last in the
// markup, the same shape as the sidebar's follow-list fit. Measured after
// every slide and on resize; the name itself ellipses as a last resort.
export function useFitRow(ref: React.RefObject<HTMLElement | null>, key: string | undefined) {
  useEffect(() => {
    const row = ref.current;
    if (!row) return;
    const fit = () => {
      const parts = Array.from(row.querySelectorAll<HTMLElement>("[data-drop]"));
      parts.forEach((p) => (p.style.display = ""));
      for (let i = parts.length - 1; i >= 0 && row.scrollWidth > row.clientWidth + 1; i--) {
        const part = parts[i];
        if (part) part.style.display = "none";
      }
      // Has the metadata dropped below the name? Measured rather than assumed
      // from a breakpoint, because a long institution name wraps the byline in
      // a narrow panel at any window width. The separating dot belongs between
      // two things on one line, never at the head of a line (31 Jul 2026, Rob).
      const lead = row.querySelector<HTMLElement>("[data-lead]");
      const trail = row.querySelector<HTMLElement>("[data-trail]");
      if (lead && trail) {
        // Line boxes, not offsetTop: two spans of different type sizes centred
        // on the same line have offsets a pixel or two apart, which read as
        // stacked and took the dot off every byline on every screen.
        const a = lead.getBoundingClientRect();
        const b = trail.getBoundingClientRect();
        row.dataset.stacked = b.top >= a.bottom - 2 ? "true" : "false";
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [ref, key]);
}

// True while at least half of the element is on screen (2 Aug 2026, Rob:
// "don't start the timer or bother rotating through them until the user is
// viewing that card"). The difference from useScrolledPast is the other
// direction: a hero that has not been reached yet is not spending slides
// either. The first slide still renders and its picture still loads, so
// arriving at it is instant; only the clock waits.
//
// Measured against the smaller of the element and the window, so a hero
// taller than the viewport still counts as visible: an intersection ratio
// alone can never reach 0.5 in that case and the slideshow would never start.
//
// With no IntersectionObserver (jsdom) it reports visible: a missing browser
// API should leave the slideshow working, not permanently paused.
export function useMajorityVisible(ref: React.RefObject<HTMLElement | null>): boolean {
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        const shown = entry.intersectionRect.height;
        const need = Math.min(entry.boundingClientRect.height, window.innerHeight) / 2;
        setVisible(need > 0 && shown >= need);
      },
      // Enough steps that the callback fires while the element crosses the
      // halfway mark, whatever proportion of the window it happens to fill.
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20) },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return visible;
}

// True once more than half the element has passed above the top of the
// window. A slideshow nobody can see should not be spending its slides
// (31 Jul 2026): the reader who scrolls down to the list comes back to the
// same practice they left, not to whichever one the clock reached.
export function useScrolledPast(ref: React.RefObject<HTMLElement | null>): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const check = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      setPast(r.height > 0 && r.top < -r.height / 2);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref]);
  return past;
}
